/**
 * The rules the avatar API applies to a snapshot profile, mirrored here so the editor can say what
 * is wrong BEFORE the save round trip.
 *
 * Mirrored, not owned: the API is the authority and refuses the same things with the same reasons.
 * What this buys is a field that turns red as it is typed instead of a red banner after a PUT -
 * and every rule below is one the API also enforces, so the two cannot disagree about what is
 * valid, only about how quickly the operator hears it.
 */

export const MIN_SNAPSHOT_SIZES = 1
export const MAX_SNAPSHOT_SIZES = 8

/** Below 16 an avatar is a smudge; above 1024 the source artwork has nothing more to give. */
export const MIN_SNAPSHOT_SIZE = 16
export const MAX_SNAPSHOT_SIZE = 1024

/** 1x to 4x. What actually bounds the cost is the raster ceiling, not this. */
export const MAX_SNAPSHOT_SCALE = 4

export const SNAPSHOT_FORMATS = ['webp', 'jpeg', 'png', 'avif'] as const
export type SnapshotFormatName = (typeof SNAPSHOT_FORMATS)[number]

export interface SnapshotProfileDraft {
  sizes: string[]
  scales: number[]
  formats: SnapshotFormatName[]
  jpegBackground: string
}

export type SizeErrorCode = 'integer' | 'range' | 'ascending' | 'raster'

export type SizeErrors = Partial<Record<number, SizeErrorCode>>

export interface ProfileErrors {
  sizes: SizeErrors
  /** A whole-form problem: no formats chosen, or a colour that is not six hex digits. */
  formats?: 'empty'
  jpegBackground?: 'hex'
}

export const sizesToValues = (sizes: number[]): string[] => sizes.map((s) => String(s))

export const valuesToSizes = (values: string[]): number[] => values.map((v) => Number(v))

/**
 * The raster a size actually encodes at a density. This is the number that matters: `sizes` are
 * CSS pixels, so 80 at 2x is a 160-pixel image.
 */
export const rasterWidth = (size: number, scale: number): number => size * scale

export function validateSnapshotProfileDraft(draft: SnapshotProfileDraft): ProfileErrors {
  const errors: ProfileErrors = { sizes: {} }
  const numbers = draft.sizes.map((v) => (v.trim() === '' ? NaN : Number(v)))
  const maxScale = draft.scales.length ? Math.max(...draft.scales) : 1

  numbers.forEach((n, i) => {
    if (!Number.isInteger(n)) {
      errors.sizes[i] = 'integer'
      return
    }
    if (n < MIN_SNAPSHOT_SIZE || n > MAX_SNAPSHOT_SIZE) {
      errors.sizes[i] = 'range'
      return
    }
    // The ceiling is on the RASTER, so it is the densest copy that has to fit: 256 at 4x is
    // exactly 1024 and fine, 512 at 4x is 2048 and is not.
    if (rasterWidth(n, maxScale) > MAX_SNAPSHOT_SIZE) {
      errors.sizes[i] = 'raster'
    }
  })

  for (let i = 1; i < numbers.length; i += 1) {
    if (
      errors.sizes[i] === undefined &&
      errors.sizes[i - 1] === undefined &&
      numbers[i] <= numbers[i - 1]
    ) {
      errors.sizes[i] = 'ascending'
    }
  }

  if (draft.formats.length === 0) {
    errors.formats = 'empty'
  }
  if (!/^[0-9A-Fa-f]{6}$/.test(draft.jpegBackground.replace(/^#/, ''))) {
    errors.jpegBackground = 'hex'
  }
  return errors
}

export const isProfileValid = (errors: ProfileErrors): boolean =>
  Object.keys(errors.sizes).length === 0 &&
  errors.formats === undefined &&
  errors.jpegBackground === undefined

/**
 * Toggling a density on or off, keeping the list sorted and 1x always present.
 *
 * 1x cannot be removed: it is the copy a plain `src` points at and the one a screen that is not
 * high-density downloads. The API refuses a list without it, so the control simply does not offer
 * it rather than letting an operator build something that will be rejected.
 */
export function toggleScale(scales: number[], scale: number): number[] {
  if (scale === 1) {
    return scales
  }
  const next = scales.includes(scale)
    ? scales.filter((s) => s !== scale)
    : [...scales, scale]
  return next.sort((a, b) => a - b)
}

/** How many objects one avatar costs under a draft, plus the `meta.json` beside them. */
export const objectsPerAvatar = (draft: SnapshotProfileDraft): number =>
  draft.sizes.length * draft.scales.length * draft.formats.length + 1
